import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  padding: 24px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: #e9ecef;
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 30px;
`;

const PropertyInfo = styled.div`
  background: #f0f8ff;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  border: 1px solid #b3d9ff;
`;

const PropertyAddress = styled.h3`
  margin: 0 0 8px 0;
  color: #0066cc;
  font-size: 18px;
`;

const PropertyDetails = styled.div`
  color: #666;
  font-size: 14px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 24px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: ${props => props.active ? '#007AFF' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#666'};
  cursor: pointer;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#0056b3' : '#e9ecef'};
  }
`;

const BookingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BookingCard = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const BookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const BookingDate = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #333;
`;

const BookingTime = styled.div`
  font-size: 16px;
  color: #666;
  margin-top: 4px;
`;

const BookingStatus = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  
  &.confirmed {
    background: #d4edda;
    color: #155724;
  }
  
  &.completed {
    background: #cce5ff;
    color: #004085;
  }
  
  &.cancelled {
    background: #f8d7da;
    color: #721c24;
  }
`;

const BookingDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailSection = styled.div`
  h4 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    margin: 4px 0;
    color: #666;
    font-size: 14px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: white;
  color: #666;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #007AFF;
    color: #007AFF;
  }
  
  &.primary {
    background: #007AFF;
    color: white;
    border-color: #007AFF;
    
    &:hover {
      background: #0056b3;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  
  h3 {
    margin: 0 0 8px 0;
    color: #999;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
`;

const BookingManagement = ({ property, onClose }) => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    // Load bookings for this property
    loadBookings();
  }, [property.id]);

  const loadBookings = async () => {
    try {
      const bookingsData = await showingbookerAPI.getPropertyBookings(property.id);
      setBookings(bookingsData || []);
    } catch (error) {
      console.error('Error loading property bookings:', error);
      setBookings([]);
    }
  };

  const getFilteredBookings = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    switch (activeTab) {
      case 'upcoming':
        return bookings.filter(booking => booking.date >= today && booking.status === 'confirmed');
      case 'completed':
        return bookings.filter(booking => booking.status === 'completed');
      case 'all':
        return bookings;
      default:
        return bookings;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleContactBuyer = (booking) => {
    const subject = `ShowingBooker: Following up on your showing at ${property.address_line1}`;
    const body = `Hi ${booking.buyerName},\n\nI wanted to follow up on your showing at ${property.address_line1} on ${formatDate(booking.date)} at ${booking.time}.\n\nPlease let me know if you have any questions!\n\nBest regards`;
    window.open(`mailto:${booking.buyerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const handleContactAgent = (booking) => {
    const subject = `ShowingBooker: Regarding showing at ${property.address_line1}`;
    const body = `Hi ${booking.agentName},\n\nRegarding the showing at ${property.address_line1} on ${formatDate(booking.date)} at ${booking.time}.\n\nConfirmation: ${booking.confirmationNumber}\n\nPlease let me know if you need anything!\n\nBest regards`;
    window.open(`mailto:${booking.agentEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const handleCopySMS = (booking) => {
    const smsText = `NOTICE TO ENTER

PREMISES: ${property.address_line1}${property.unit_number ? `, #${property.unit_number}` : ''}, ${property.city}, ${property.state} ${property.zip_code}

INSPECTION DATE: ${formatDate(booking.date)}
INSPECTION TIME: ${booking.time}

PURPOSE: To exhibit dwelling to prospective purchasers per CA Civil Code §1954

ENTERING:
- ${booking.agentName}, Agent
- ${booking.buyerName}, Buyer

Please:
- Be present
- Turn on lights
- Secure pets
- Ensure access to entrance & unit

Confirmation: ${booking.confirmationNumber}

Powered by ShowingBooker.com`;
    
    navigator.clipboard.writeText(smsText).then(() => {
      alert('Notice to Enter copied to clipboard! You can now paste it in your Messages app.');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = smsText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Notice to Enter copied to clipboard!');
    });
  };

  const filteredBookings = getFilteredBookings();

  return (
    <Modal onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>📅 Booking Management</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <PropertyInfo>
            <PropertyAddress>
              {property.address_line1}
              {property.unit_number && `, ${property.unit_number}`}
            </PropertyAddress>
            <PropertyDetails>
              {property.city}, {property.state} {property.zip_code}
            </PropertyDetails>
          </PropertyInfo>

          <TabContainer>
            <Tab 
              active={activeTab === 'upcoming'} 
              onClick={() => setActiveTab('upcoming')}
            >
              📋 Upcoming ({bookings.filter(b => b.date >= new Date().toISOString().split('T')[0] && b.status === 'confirmed').length})
            </Tab>
            <Tab 
              active={activeTab === 'completed'} 
              onClick={() => setActiveTab('completed')}
            >
              ✅ Completed ({bookings.filter(b => b.status === 'completed').length})
            </Tab>
            <Tab 
              active={activeTab === 'all'} 
              onClick={() => setActiveTab('all')}
            >
              📊 All Bookings ({bookings.length})
            </Tab>
          </TabContainer>

          <BookingsList>
            {filteredBookings.length > 0 ? (
              filteredBookings.map((booking) => (
                <BookingCard key={booking.id}>
                  <BookingHeader>
                    <div>
                      <BookingDate>{formatDate(booking.date)}</BookingDate>
                      <BookingTime>{booking.time} (30 minutes)</BookingTime>
                    </div>
                    <BookingStatus className={booking.status}>
                      {booking.status}
                    </BookingStatus>
                  </BookingHeader>

                  <BookingDetails>
                    <DetailSection>
                      <h4>👤 Buyer Information</h4>
                      <p><strong>Name:</strong> {booking.buyerName}</p>
                      <p><strong>Email:</strong> {booking.buyerEmail}</p>
                      <p><strong>Phone:</strong> {booking.buyerPhone}</p>
                    </DetailSection>

                    <DetailSection>
                      <h4>🏢 Agent Information</h4>
                      <p><strong>Agent:</strong> {booking.agentName}</p>
                      <p><strong>Email:</strong> {booking.agentEmail}</p>
                      <p><strong>Phone:</strong> {booking.agentPhone}</p>
                    </DetailSection>
                  </BookingDetails>

                  {booking.specialRequests && (
                    <DetailSection style={{ marginBottom: '16px' }}>
                      <h4>📝 Special Requests</h4>
                      <p>{booking.specialRequests}</p>
                    </DetailSection>
                  )}

                  <DetailSection style={{ marginBottom: '16px' }}>
                    <h4>🔍 Booking Details</h4>
                    <p><strong>Confirmation:</strong> {booking.confirmationNumber}</p>
                    <p><strong>Booked:</strong> {new Date(booking.createdAt).toLocaleDateString()}</p>
                  </DetailSection>

                  <ActionButtons>
                    <ActionButton 
                      className="primary"
                      onClick={() => handleCopySMS(booking)}
                    >
                      📋 Copy Notice to Enter
                    </ActionButton>
                    <ActionButton onClick={() => handleContactBuyer(booking)}>
                      📧 Email Buyer
                    </ActionButton>
                    <ActionButton onClick={() => handleContactAgent(booking)}>
                      📧 Email Agent
                    </ActionButton>
                    <ActionButton onClick={() => window.open(`tel:${booking.buyerPhone.replace(/\D/g, '')}`)}>
                      📞 Call Buyer
                    </ActionButton>
                    <ActionButton onClick={() => window.open(`tel:${booking.agentPhone.replace(/\D/g, '')}`)}>
                      📞 Call Agent
                    </ActionButton>
                  </ActionButtons>
                </BookingCard>
              ))
            ) : (
              <EmptyState>
                <h3>
                  {activeTab === 'upcoming' && '📅 No upcoming bookings'}
                  {activeTab === 'completed' && '✅ No completed bookings'}
                  {activeTab === 'all' && '📊 No bookings yet'}
                </h3>
                <p>
                  {activeTab === 'upcoming' && 'New bookings will appear here when they are scheduled.'}
                  {activeTab === 'completed' && 'Completed showings will appear here after they finish.'}
                  {activeTab === 'all' && 'Share your property booking link to start receiving bookings!'}
                </p>
              </EmptyState>
            )}
          </BookingsList>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BookingManagement;